import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { GIVING } from "../lib/giving";
import { Dialog, DialogContent, DialogTitle, DialogClose } from "./ui/dialog";
import { Gift, ShieldCheck, Copy, Check, Landmark, Mail, ExternalLink, CreditCard, X } from "lucide-react";

const NAVY = "#0d2340";
const GREEN = "#1E8A5B";

function CopyBtn({ text, label, testid }) {
  const [done, setDone] = useState(false);
  const copy = () => {
    const finish = () => { setDone(true); toast.success(`${label} copied`); setTimeout(() => setDone(false), 1800); };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(finish).catch(() => toast.error("Copy failed — long-press to copy"));
    else { toast.error("Long-press the text to copy"); }
  };
  return (
    <button type="button" data-testid={testid} onClick={copy}
      className="shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-muted/60"
      style={{ borderColor: done ? GREEN : "rgba(13,35,64,0.2)", color: done ? GREEN : NAVY }}>
      {done ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
    </button>
  );
}

function Method({ icon: Icon, title, subtitle, value, copyLabel, testid, children }) {
  return (
    <div className="rounded-2xl border p-4" style={{ borderColor: "rgba(13,35,64,0.12)" }} data-testid={testid}>
      <div className="flex items-center gap-2.5">
        <div className="h-9 w-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--tenant-accent, #E0A73A)22" }}>
          <Icon className="h-4.5 w-4.5" style={{ color: "var(--tenant-accent, #E0A73A)" }} />
        </div>
        <div className="font-heading font-bold text-[15px]" style={{ color: NAVY }}>{title}</div>
      </div>
      {subtitle && <p className="text-[13px] text-muted-foreground mt-2 leading-snug">{subtitle}</p>}
      {value && (
        <div className="mt-2.5 flex items-center gap-2">
          <div className="flex-1 min-w-0 rounded-xl bg-muted/50 px-3 py-2 text-sm font-medium truncate" style={{ color: NAVY }}>{value}</div>
          <CopyBtn text={value} label={copyLabel} testid={`${testid}-copy`} />
        </div>
      )}
      {children}
    </div>
  );
}

export default function GiveDialog({ open, onOpenChange, donateUrl, donateLabel, orgSlug }) {
  useEffect(() => { if (open) api.post("/give-clicks").catch(() => {}); }, [open]);
  // HIM (Hepler Int'l Ministries) tax-deductible methods apply to RESET only.
  // Other orgs see only their own online donate link. Per-org config is on the roadmap.
  const isReset = orgSlug === "reset";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 border-0 overflow-hidden rounded-3xl w-[calc(100vw-24px)] max-w-md [&>button]:hidden" data-testid="give-dialog">
        {/* Compact banner header (matches page headers) with an obvious close */}
        <div className="flex items-center justify-between gap-3 px-5 py-4" style={{ background: NAVY }}>
          <DialogTitle className="font-heading font-bold text-white text-xl flex items-center gap-2 min-w-0">
            <Gift className="h-5 w-5 shrink-0" style={{ color: "#E0A73A" }} /> <span className="truncate">Give to this mission</span>
          </DialogTitle>
          <DialogClose data-testid="give-close" aria-label="Close"
            className="h-9 w-9 shrink-0 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors">
            <X className="h-5 w-5" />
          </DialogClose>
        </div>

        <div className="px-5 py-5 max-h-[68vh] overflow-y-auto">
          {isReset && GIVING.taxDeductible && (
            <div className="rounded-2xl p-3 flex items-start gap-2.5" style={{ background: "rgba(30,138,91,0.08)", border: "1px solid rgba(30,138,91,0.25)" }} data-testid="give-tax-note">
              <ShieldCheck className="h-5 w-5 shrink-0 mt-0.5" style={{ color: GREEN }} />
              <div className="text-[13px] leading-snug" style={{ color: "#2C2C3E" }}>
                <span className="font-bold" style={{ color: GREEN }}>Tax-deductible</span> — processed through {GIVING.processor}, a 501(c)(3). Receipt mailed to you.
              </div>
            </div>
          )}

          <div className="mt-4" data-testid="give-suggested-amounts">
            <div className="text-[13px] text-muted-foreground mb-2">Every gift matters — many partners give:</div>
            <div className="flex gap-2">
              {["$25", "$50", "$100"].map((a) => (
                <div key={a} className="flex-1 min-w-0 text-center rounded-xl border px-2 py-2.5 text-[15px] font-bold" style={{ borderColor: "rgba(13,35,64,0.14)", color: NAVY }}>{a}</div>
              ))}
            </div>
            <div className="text-[12px] text-muted-foreground mt-2 text-center">…or any amount that's on your heart.</div>
          </div>

          <div className="mt-4 space-y-3">
            {isReset && (<>
              <Method icon={CreditCard} title="PayPal" testid="give-paypal"
                subtitle="Send your gift in PayPal to the address below."
                value={GIVING.paypalEmail} copyLabel="PayPal email">
                <a href="https://www.paypal.com/myaccount/transfer/homepage/pay" target="_blank" rel="noopener noreferrer"
                  data-testid="give-paypal-open" onClick={() => api.post("/give-clicks").catch(() => {})}
                  className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: "var(--tenant-accent, #E0A73A)" }}>
                  Open PayPal <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </Method>

              <Method icon={Landmark} title="Zelle" testid="give-zelle"
                subtitle="Open your bank's app, choose Zelle, and send to this email."
                value={GIVING.zelleEmail} copyLabel="Zelle email" />

              <Method icon={Mail} title="Mail a check" testid="give-check"
                subtitle={`Make the check payable to "${GIVING.checkPayableTo}". In the memo line, write: ${GIVING.checkMemo}.`}
                value={GIVING.checkAddress} copyLabel="Mailing address" />
            </>)}

            {!isReset && !donateUrl && (
              <p className="text-sm text-muted-foreground text-center py-2" data-testid="give-none">
                Giving options for this mission aren't set up yet — please reach out to your mission leader.
              </p>
            )}
          </div>

          {donateUrl && (
            <div className="mt-5 pt-4 border-t text-center" style={{ borderColor: "rgba(13,35,64,0.1)" }}>
              <a href={donateUrl} target="_blank" rel="noopener noreferrer" data-testid="give-online-btn"
                onClick={() => api.post("/give-clicks").catch(() => {})}
                className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: NAVY }}>
                Prefer more options? {donateLabel ? `${donateLabel} online` : "Visit our website to give"} <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          )}

          <DialogClose asChild>
            <button data-testid="give-close-bottom" className="w-full mt-4 rounded-full h-11 text-sm font-semibold border transition-colors hover:bg-muted/50" style={{ borderColor: "rgba(13,35,64,0.15)", color: NAVY }}>Close</button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
